import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faElevator, faStairs } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// CUSTOM HOOKS
import useGetAllBanksLight from "../../../customHooks/GET_requests/useGetAllBanksLight";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankLightInterface } from "../../../interfaces/Bank.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";

function ReportsFilterGetBanks() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters, blockFilters } = useSelector(
    (state: RootState) => state.report
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { banksLight, isLoadingGetAllBanksLight } = useGetAllBanksLight();

  // ************************************************ */
  // FILTERED DATA ********************************** */
  const filteredBanks = useMemo(() => {
    if (filters.liftType === "ALL") return banksLight;
    return banksLight.filter(
      (bank: BankLightInterface) => bank.type === filters.liftType
    );
  }, [banksLight, filters.liftType]);

  // ************************************************ */
  // GROUPED DATA *********************************** */
  const serverGroups = useMemo(() => {
    const serverMap = new Map<string, { id: string; name: string }>();
    filteredBanks.forEach((bank: BankLightInterface) => {
      if (bank.channel?.server && !serverMap.has(bank.channel.server.id)) {
        serverMap.set(bank.channel.server.id, bank.channel.server);
      }
    });
    return Array.from(serverMap.values());
  }, [filteredBanks]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeBank = (newValue: string) => {
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    const bank = banksLight.find(
      (b: BankLightInterface) => b.id === newValue
    );
    if (bank) {
      copy.bankId = bank.id;
      copy.bankName = bank.name;
      if (bank.channel?.server) {
        copy.serverId = bank.channel.server.id;
        copy.serverName = bank.channel.server.name;
      }
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (filteredBanks.length > 0 && filters.bankId === "") {
      const sorted = [...filteredBanks].sort(
        (a: BankLightInterface, b: BankLightInterface) =>
          a.name.localeCompare(b.name)
      );
      onChangeBank(sorted[0].id);
    }
  }, [filteredBanks]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: 'flex', alignItems: 'center' }}>
      <Button size="small" disabled style={{ minWidth: 'fit-content' }}>
        {t("report.selectBank")}
      </Button>
      <Tooltip title={filteredBanks.length === 0 ? t("report.noBanks") : ""}>
        <Select
          className="generalStyles__width100"
          disabled={filteredBanks.length === 0 || blockFilters}
          showSearch
          optionFilterProp="label"
          placeholder={t("report.selectBank")}
          value={filters.bankId === "" ? null : filters.bankId}
          size="small"
          onChange={onChangeBank}
          loading={isLoadingGetAllBanksLight}
        >
          {serverGroups.map((serv) => (
            <OptGroup key={serv.id} label={`SERVER / ${serv.name}`}>
              {filteredBanks
                .filter(
                  (bank: BankLightInterface) =>
                    bank.channel?.server?.id === serv.id
                )
                .sort((a: BankLightInterface, b: BankLightInterface) =>
                  a.name.localeCompare(b.name)
                )
                .map((bank: BankLightInterface) => (
                  <Option key={bank.id} value={bank.id} label={bank.name}>
                    <Tooltip
                      overlayInnerStyle={{ minWidth: '380px' }}
                      placement="right"
                      title={
                        <div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Type</div>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '6px', paddingBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {bank.type === "ELEVATOR" && <FontAwesomeIcon icon={faElevator} />}
                            {bank.type === "ESCALATOR" && <FontAwesomeIcon icon={faStairs} />}
                            <b className="generalStyles__info">{bank.type}</b>
                          </div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Bank</div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {bank.type === "ELEVATOR" && <FontAwesomeIcon icon={faElevator} />}
                            {bank.type === "ESCALATOR" && <FontAwesomeIcon icon={faStairs} />}
                            <b className="generalStyles__info">{bank.name}</b>
                          </div>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '6px', paddingBottom: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{bank.id}</span></span>
                              <CopyToClipboard text={bank.id} onCopy={() => message.success('Bank ID copied')}>
                                <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                              </CopyToClipboard>
                            </div>
                          </div>

                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Channel</div>
                          <div><b className="generalStyles__info">{bank.channel?.name}</b></div>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '6px', paddingBottom: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{bank.channel?.id}</span></span>
                              <CopyToClipboard text={bank.channel?.id || ''} onCopy={() => message.success('Channel ID copied')}>
                                <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                              </CopyToClipboard>
                            </div>
                          </div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Server</div>
                          <div><b className="generalStyles__info">{bank.channel?.server?.name}</b></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{bank.channel?.server?.id}</span></span>
                            <CopyToClipboard text={bank.channel?.server?.id || ''} onCopy={() => message.success('Server ID copied')}>
                              <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                            </CopyToClipboard>
                          </div>

                        </div>
                      }
                    >
                      {bank.type === "ELEVATOR" && <FontAwesomeIcon icon={faElevator} style={{ marginRight: '8px' }} />}
                      {bank.type === "ESCALATOR" && <FontAwesomeIcon icon={faStairs} style={{ marginRight: '8px' }} />}
                      {bank.name}
                    </Tooltip>
                  </Option>
                ))}
            </OptGroup>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetBanks;
